// UI Theme
import "@/styles/index.scss";

import { checkTokenExpired } from "@/composables/_commons/auth.js";

import { createApp } from "vue";
import { sync } from "vuex-router-sync";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import PrimeVue from "primevue/config";
import Ripple from "primevue/ripple";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";

import {
  // Plugins
  create$Fetch,
  createApplicationInsights,
  createAxios,
  createAwsS3,
  createKeycloak,
  createMsal,
  createAuthorization,
  createRouter,
  createStore,
  createWebVitals,

  // Directives
  roles,
  sortable,

  // Composables
  // useDev,

  // Config
  getAppConfig,
} from "@sttedra_ui/framework";

import App from "./App.vue";
import { routes, hooks, routerOptions } from "./routes";
import { suppressMsalLogs } from "@/composables/_commons/suppressMsalLogs";

suppressMsalLogs();

// (!) Show all STTEDRA logs
// useDev.showAll(); useDev.hideAll(); useDev.show('YOUR_REGEXP');

// #region Creation
const {
  $fetchConfig,
  applicationInsightsConfig,
  axiosConfig,
  awsConfig,
  isProduction,
  keycloakConfig,
  msalConfig,
  webVitalsConfig,
} = getAppConfig();

import ConfirmationService from "primevue/confirmationservice";
import index from "@/plugins/store/index";

const app = createApp(App);
const router = createRouter(routes, hooks, { routerOptions });

const appInsights = createApplicationInsights(applicationInsightsConfig);
const webVitals = createWebVitals(webVitalsConfig);
const $fetch = create$Fetch();
const axios = createAxios();
const awsS3 = createAwsS3();
const keycloak = createKeycloak(keycloakConfig);
const msal = createMsal(msalConfig);

const store = createStore(isProduction);
store.registerModule("vuex", index);

const authorization = createAuthorization();
// #endregion

// #region Mount
sync(store, router);

if (appInsights) {
  app.use(appInsights);
}

if (webVitals) {
  app.use(webVitals);
}

if (keycloak) {
  app.use(keycloak);
}

if (msal) {
  app.use(msal);
}

app.use($fetch, $fetchConfig);
app.use(PrimeVue, {
  ripple: false,
  locale: {
    dayNames: [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ],
    dayNamesShort: ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"],
    dayNamesMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
    monthNames: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
    monthNamesShort: [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ],
    dateFormat: "dd/mm/yy",
    today: "Hoy",
    firstDayOfWeek: 1,
    accept: "Aceptar",
    reject: "Rechazar",
    clear: "Limpiar",
    weak: "Débil",
    medium: "Medio",
    strong: "Fuerte",
    pending: "Pendiente",
  },
});
app.use(ToastService);
app.use(axios, axiosConfig);
app.use(awsS3, awsConfig);
app.use(router);
app.use(store);
app.use(ConfirmationService);
app.use(autoAnimatePlugin);
app.use(authorization, { api: async () => [] });

app.directive("sortable", sortable);
app.directive("roles", roles);
app.directive("ripple", Ripple);
app.directive("tooltip", Tooltip);

let vm = null;
router.isReady().then(async () => {
  await checkTokenExpired({ expectedResponse: false });
  vm = app.mount("#" + VUE_APP_ID);
});

// #endregion

export { vm, store }; // window.sttedra._root.vm
